import { Loader } from '@/components/prompt-kit/loader'
import {
  PromptInput,
  PromptInputAction,
  PromptInputActions,
  PromptInputTextarea,
} from '@/components/prompt-kit/prompt-input'
import { Badge } from '@/components/shadcn/badge'
import { Button } from '@/components/shadcn/button'
import { useAiChatComponentContext } from './useAiChatComponentContext'
import { ContextWindowIndicator } from './ContextWindowIndicator'
import { ChatContextState, ChatContextEventName } from './types'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { ArrowUp, Square, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

export const AI_CHAT_CONTEXT_DRAG_MIME = 'application/x-ai-chat-context-item'

export type ChatInputSendPayload = {
  displayText: string
  selectedContextKeys: string[]
}

interface ChatInputProps {
  /** Whether a message is currently being submitted */
  isSubmitting: boolean
  /** Whether a stop was requested and we're waiting for confirmation */
  isStopping?: boolean
  /** Whether cancel/stop is available for the in-flight turn */
  canStop?: boolean
  /** Whether the worker emitted the `started` envelope for this turn */
  workerHasStarted?: boolean
  /** Whether there are messages in the chat */
  hasMessages: boolean
  /** Callback when user submits a message */
  onSendMessage: (payload: ChatInputSendPayload) => void
  /** Callback to stop the in-flight agent turn */
  onStop?: () => void
  /** Callback to clear chat messages */
  onClearMessages: () => void
  /** Latest agent context window telemetry */
  contextState?: ChatContextState | null
  /** Most recent context-management SignalR event */
  lastContextEvent?: ChatContextEventName | null
}

/**
 * Chat input component with prompt textarea and action buttons.
 */
export function ChatInput({
  isSubmitting,
  isStopping = false,
  canStop = false,
  workerHasStarted = false,
  hasMessages,
  onSendMessage,
  onStop,
  onClearMessages,
  contextState = null,
  lastContextEvent = null,
}: ChatInputProps) {
  const { selectedContext } = useAiChatComponentContext()
  const [prompt, setPrompt] = useState('')
  const [selectedContextKeys, setSelectedContextKeys] = useState<string[]>([])
  const [mentionState, setMentionState] = useState<{
    open: boolean
    query: string
    startIndex: number
    endIndex: number
    highlightedIndex: number
  }>({
    open: false,
    query: '',
    startIndex: -1,
    endIndex: -1,
    highlightedIndex: 0,
  })
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const mentionOptionRefs = useRef<Array<HTMLButtonElement | null>>([])
  const FEATURE_ENABLE_CONTEXT_MENTION_AND_DROP = true

  const selectedContextMap = useMemo(
    () => Object.fromEntries(selectedContext.map((entry) => [entry.selectionKey, entry])),
    [selectedContext]
  )

  const mentionSuggestions = useMemo(() => {
    const normalizedQuery = mentionState.query.trim().toLowerCase()
    const candidates = selectedContext.filter((entry) => {
      if (!normalizedQuery) return true
      const label = getContextLabel(entry).toLowerCase()
      const fallback = `${entry.componentType} ${entry.componentId}`.toLowerCase()
      return label.includes(normalizedQuery) || fallback.includes(normalizedQuery)
    })
    return candidates.slice(0, 8)
  }, [mentionState.query, selectedContext])

  const syncedSelectedContextKeys = useMemo(
    () => selectedContextKeys.filter((selectionKey) => Boolean(selectedContextMap[selectionKey])),
    [selectedContextKeys, selectedContextMap]
  )

  // Handle form submission
  const handleSubmit = useCallback(() => {
    const normalizedPrompt = prompt.trim()
    if ((normalizedPrompt || syncedSelectedContextKeys.length > 0) && !isSubmitting) {
      onSendMessage({
        displayText: prompt,
        selectedContextKeys: syncedSelectedContextKeys,
      })
      setPrompt('')
      setSelectedContextKeys([])
      setMentionState({ open: false, query: '', startIndex: -1, endIndex: -1, highlightedIndex: 0 })
    }
  }, [isSubmitting, onSendMessage, prompt, syncedSelectedContextKeys])

  // Handle clear chat
  const handleClear = useCallback(() => {
    onClearMessages()
    toast.success('Chat cleared')
  }, [onClearMessages])

  const updateMentionState = useCallback(
    (text: string, cursorIndex: number) => {
      if (!FEATURE_ENABLE_CONTEXT_MENTION_AND_DROP) return
      const textBeforeCursor = text.slice(0, cursorIndex)
      const match = textBeforeCursor.match(/(^|\s)@([^\s@]*)$/)

      if (!match) {
        setMentionState((previous) => ({ ...previous, open: false, query: '', startIndex: -1, endIndex: -1 }))
        return
      }

      const query = match[2] ?? ''
      const startIndex = cursorIndex - query.length - 1
      setMentionState((previous) => ({
        ...previous,
        open: true,
        query,
        startIndex,
        endIndex: cursorIndex,
        highlightedIndex:
          previous.open &&
          previous.query === query &&
          previous.startIndex === startIndex &&
          previous.endIndex === cursorIndex
            ? previous.highlightedIndex
            : 0,
      }))
    },
    [FEATURE_ENABLE_CONTEXT_MENTION_AND_DROP]
  )

  const updateCaretAndMentionsFromEvent = useCallback(
    (event: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const target = event.currentTarget
      textareaRef.current = target
      const cursorIndex = target.selectionStart ?? 0
      updateMentionState(prompt, cursorIndex)
    },
    [prompt, updateMentionState]
  )

  const syncPromptAndMentions = useCallback(
    (nextPrompt: string) => {
      setPrompt(nextPrompt)
      requestAnimationFrame(() => {
        const activeTextarea = textareaRef.current ?? (document.activeElement as HTMLTextAreaElement | null)
        const cursorIndex = activeTextarea?.selectionStart ?? nextPrompt.length
        updateMentionState(nextPrompt, cursorIndex)
      })
    },
    [updateMentionState]
  )

  const addSelectedContextKey = useCallback((selectionKey: string) => {
    setSelectedContextKeys((previous) => (previous.includes(selectionKey) ? previous : [...previous, selectionKey]))
  }, [])

  const insertMention = useCallback(
    (entry: (typeof selectedContext)[number]) => {
      const label = getContextLabel(entry)
      const prefix = prompt.slice(0, mentionState.startIndex)
      const suffix = prompt.slice(mentionState.endIndex)
      const mentionText = `[@${label}] `
      const nextPrompt = `${prefix}${mentionText}${suffix}`
      syncPromptAndMentions(nextPrompt)
      addSelectedContextKey(entry.selectionKey)

      requestAnimationFrame(() => {
        const nextCursor = prefix.length + mentionText.length
        textareaRef.current?.focus()
        textareaRef.current?.setSelectionRange(nextCursor, nextCursor)
      })
      setMentionState((previous) => ({ ...previous, open: false }))
    },
    [
      addSelectedContextKey,
      mentionState.endIndex,
      mentionState.startIndex,
      prompt,
      selectedContext,
      syncPromptAndMentions,
    ]
  )

  const insertDroppedContextMention = useCallback(
    (selectionKey: string) => {
      const entry = selectedContextMap[selectionKey]
      if (!entry) return
      const label = getContextLabel(entry)
      const activeTextarea = textareaRef.current
      const cursorIndex = activeTextarea?.selectionStart ?? prompt.length
      const mentionText = `[@${label}] `
      const nextPrompt = `${prompt.slice(0, cursorIndex)}${mentionText}${prompt.slice(cursorIndex)}`
      syncPromptAndMentions(nextPrompt)
      addSelectedContextKey(selectionKey)
      requestAnimationFrame(() => {
        const nextCursor = cursorIndex + mentionText.length
        textareaRef.current?.focus()
        textareaRef.current?.setSelectionRange(nextCursor, nextCursor)
      })
    },
    [addSelectedContextKey, prompt, selectedContextMap, syncPromptAndMentions]
  )

  const removeSelectedContextKey = useCallback(
    (selectionKey: string) => {
      const targetEntry = selectedContextMap[selectionKey]
      setSelectedContextKeys((previous) => previous.filter((id) => id !== selectionKey))
      if (!targetEntry) return

      const label = getContextLabel(targetEntry)
      const mentionToken = `@${label}`
      const bracketedMentionToken = `[@${label}]`
      const nextPrompt = prompt
        .split(bracketedMentionToken)
        .join('')
        .split(mentionToken)
        .join('')
        .replace(/\s{2,}/g, ' ')
        .trimStart()
      syncPromptAndMentions(nextPrompt)
    },
    [prompt, selectedContextMap, syncPromptAndMentions]
  )

  const handleTextareaKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!FEATURE_ENABLE_CONTEXT_MENTION_AND_DROP || !mentionState.open || mentionSuggestions.length === 0) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setMentionState((previous) => ({
          ...previous,
          highlightedIndex: (previous.highlightedIndex + 1) % mentionSuggestions.length,
        }))
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setMentionState((previous) => ({
          ...previous,
          highlightedIndex: (previous.highlightedIndex - 1 + mentionSuggestions.length) % mentionSuggestions.length,
        }))
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault()
        insertMention(mentionSuggestions[mentionState.highlightedIndex] ?? mentionSuggestions[0])
        return
      }
      if (event.key === 'Escape') {
        event.preventDefault()
        setMentionState((previous) => ({ ...previous, open: false }))
      }
    },
    [
      FEATURE_ENABLE_CONTEXT_MENTION_AND_DROP,
      insertMention,
      mentionState.highlightedIndex,
      mentionState.open,
      mentionSuggestions,
    ]
  )

  const handleTextareaDrop = useCallback(
    (event: React.DragEvent<HTMLTextAreaElement>) => {
      if (!FEATURE_ENABLE_CONTEXT_MENTION_AND_DROP) return
      const droppedSelectionKey = event.dataTransfer.getData(AI_CHAT_CONTEXT_DRAG_MIME)
      if (!droppedSelectionKey) return
      event.preventDefault()
      insertDroppedContextMention(droppedSelectionKey)
    },
    [FEATURE_ENABLE_CONTEXT_MENTION_AND_DROP, insertDroppedContextMention]
  )

  const handleTextareaDragOver = useCallback((event: React.DragEvent<HTMLTextAreaElement>) => {
    if (!FEATURE_ENABLE_CONTEXT_MENTION_AND_DROP) return
    const hasContextPayload = Array.from(event.dataTransfer.types).includes(AI_CHAT_CONTEXT_DRAG_MIME)
    if (!hasContextPayload) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  }, [])

  useEffect(() => {
    if (!mentionState.open) return
    const highlightedOption = mentionOptionRefs.current[mentionState.highlightedIndex]
    highlightedOption?.scrollIntoView({ block: 'nearest' })
  }, [mentionState.highlightedIndex, mentionState.open])

  return (
    // <div className="shrink-0 border-t border-border bg-offwhite px-6 py-4">
    <div className="shrink-0 bg-offwhite px-6 py-4">
      {/* <div className="mx-auto max-w-3xl"> */}
      <div className="mx-auto max-w-4xl">
        <PromptInput
          isLoading={isSubmitting}
          value={prompt}
          onValueChange={syncPromptAndMentions}
          onSubmit={handleSubmit}
          disabled={isSubmitting}
          // className="rounded-2xl border border-input bg-offwhite p-0"
          className="rounded-2xl border border-input bg-background p-0"
          autoFocus
        >
          <div className="flex flex-col">
            {syncedSelectedContextKeys.length > 0 && (
              <div className="flex flex-wrap gap-2 px-4 pt-3">
                {syncedSelectedContextKeys.map((selectionKey) => {
                  const entry = selectedContextMap[selectionKey]
                  if (!entry) return null

                  return (
                    <Badge key={selectionKey} variant="secondary" className="inline-flex items-center gap-1">
                      <span className="max-w-52 truncate">{getContextLabel(entry)}</span>
                      <button
                        type="button"
                        className="rounded-sm p-0.5 hover:bg-background/80"
                        onClick={() => removeSelectedContextKey(selectionKey)}
                        aria-label={`Remove ${getContextLabel(entry)} context token`}
                      >
                        <IconCloseOutline className="size-3" />
                      </button>
                    </Badge>
                  )
                })}
              </div>
            )}
            <PromptInputTextarea
              placeholder="Ask anything about this app..."
              className="min-h-[52px] resize-none border-0 px-4 pt-3 text-base text-foreground focus-visible:ring-0"
              onSelect={updateCaretAndMentionsFromEvent}
              onClick={updateCaretAndMentionsFromEvent}
              onKeyUp={updateCaretAndMentionsFromEvent}
              onFocus={updateCaretAndMentionsFromEvent}
              onKeyDown={handleTextareaKeyDown}
              onDrop={handleTextareaDrop}
              onDragOver={handleTextareaDragOver}
            />
            {mentionState.open && mentionSuggestions.length > 0 && (
              <div className="px-3 pb-2">
                <div className="max-h-40 overflow-auto rounded-md border bg-background p-1">
                  {mentionSuggestions.map((entry, index) => (
                    <button
                      key={entry.selectionKey}
                      ref={(element) => {
                        mentionOptionRefs.current[index] = element
                      }}
                      type="button"
                      className={`flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-xs ${
                        index === mentionState.highlightedIndex ? 'bg-muted' : 'hover:bg-muted/50'
                      }`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => insertMention(entry)}
                    >
                      <span className="truncate">{getContextLabel(entry)}</span>
                      <span className="ml-2 text-muted-foreground">{entry.componentType}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <PromptInputActions className="flex w-full items-center justify-between px-3 pb-3">
              <div className="flex items-center gap-2">
                {hasMessages && (
                  <PromptInputAction tooltip="Clear chat">
                    <Button variant="ghost" size="icon" className="size-8 rounded-full" onClick={handleClear}>
                      <Trash2 className="size-4" />
                    </Button>
                  </PromptInputAction>
                )}
              </div>

              <div className="flex items-center gap-5">
                <ContextWindowIndicator contextState={contextState} lastContextEvent={lastContextEvent} />

                {isSubmitting && isStopping ? (
                  <div className="flex size-8 items-center justify-center" aria-label="Stopping agent" role="status">
                    <Loader variant="circular" size="sm" />
                  </div>
                ) : isSubmitting && !canStop ? (
                  <PromptInputAction tooltip="Waiting for the agent to connect to this chat before stop is available">
                    <div
                      className="flex size-8 items-center justify-center"
                      aria-label="Waiting for agent"
                      role="status"
                    >
                      <Loader variant="circular" size="sm" />
                    </div>
                  </PromptInputAction>
                ) : isSubmitting && canStop && onStop ? (
                  <PromptInputAction
                    disableWithInput={false}
                    tooltip={
                      workerHasStarted ? 'Stop agent' : 'Cancel request — the agent has not connected to this chat yet'
                    }
                  >
                    <Button
                      type="button"
                      size="icon"
                      variant="destructive"
                      onClick={onStop}
                      className="size-8 rounded-full"
                      aria-label={workerHasStarted ? 'Stop agent' : 'Cancel request'}
                    >
                      <Square className="size-3.5 fill-current" />
                    </Button>
                  </PromptInputAction>
                ) : (
                  <Button
                    size="icon"
                    disabled={!(prompt.trim() || syncedSelectedContextKeys.length > 0) || isSubmitting}
                    onClick={handleSubmit}
                    className="size-8 rounded-full"
                  >
                    <ArrowUp className="size-4" />
                  </Button>
                )}
              </div>
            </PromptInputActions>
          </div>
        </PromptInput>
      </div>
    </div>
  )
}

function getContextLabel(entry: ReturnType<typeof useAiChatComponentContext>['selectedContext'][number]): string {
  const response = entry.response
  if (response && typeof response === 'object') {
    const responseRecord = response as Record<string, unknown>
    const uiSchema = responseRecord.uiSchema
    if (uiSchema && typeof uiSchema === 'object') {
      const uiSchemaRecord = uiSchema as Record<string, unknown>
      if (typeof uiSchemaRecord.title === 'string' && uiSchemaRecord.title.trim()) {
        return uiSchemaRecord.title
      }
      const schema = uiSchemaRecord.schema
      if (schema && typeof schema === 'object') {
        const schemaRecord = schema as Record<string, unknown>
        if (typeof schemaRecord.title === 'string' && schemaRecord.title.trim()) {
          return schemaRecord.title
        }
      }
      const tableConfig = uiSchemaRecord.tableConfig
      if (tableConfig && typeof tableConfig === 'object') {
        const tableConfigRecord = tableConfig as Record<string, unknown>
        if (typeof tableConfigRecord.title === 'string' && tableConfigRecord.title.trim()) {
          return tableConfigRecord.title
        }
      }
    }
  }
  return `${entry.componentType}:${entry.componentId}`
}
